import type { MonetaryValue } from 'ezmoney';
import isNil from 'lodash/isNil';

import type {
  CreditNote,
  CreditNoteValidationAction,
} from '@finance-review/models/credit-note';
import {
  type PayableAutomation,
  type PayableAmortisation,
} from 'modules/payable/models';
import {
  type UkVehicleFuelType,
  type UkVehicleEngineSize,
} from 'modules/requests/mileage/creation/model';
import { type ExpenseBeneficiary } from 'src/core/modules/requests/expense/api/useExpenseBenefericiariesQuery';

import type { PayableType, PayableState } from '../../../../models/payable';

export type PayableSubType =
  | 'creditNote'
  | 'multiUseCard'
  | 'plasticCard'
  | 'subscriptionCard'
  | 'singleUseCard'
  | 'creditNote';

export type Member =
  | {
      type: 'user';
      id: string;
      givenName: string;
      familyName: string;
      email: string;
      avatar: string | undefined;
    }
  | {
      type: 'suspendedUser';
      id: string;
      givenName: string;
      familyName: string;
      avatar: string | undefined;
    }
  | { type: 'invitee'; id: string; email: string };

export type Supplier = {
  id: string;
  name: string;
  thumbnailUrl: string | undefined;
};

export type Team = {
  id: string;
  name: string;
};

export type CostCenter = {
  id: string;
  name: string;
};

type AccountPayable = {
  id: string;
  generalAccountCode: string;
  auxiliaryAccountCode: string | undefined;
};

type SpainEreceiptCompliance = {
  receipt: {
    path: string;
    uniqueId: string;
  };
  closure?: {
    closedAt: string;
    closingPeriod: string;
  };
};

type Compliances = {
  spainEreceipt?: SpainEreceiptCompliance | null;
};

export type DocumentaryEvidence = (
  | {
      type:
        | 'invoice'
        | 'declarationOfMissingInvoice'
        | 'perDiemEntry'
        | 'mileageEntry';
      invoiceNumber: string | undefined;
    }
  | { type: 'creditNote'; creditNoteNumber: string | undefined }
) & {
  compliances?: Compliances;
  validity: { isValid: boolean; reason: string | undefined };
  attachments: {
    mediaType: string;
    url: string;
    path: string;
    downloadUrl: string;
    thumbnailUrl: string;
    downloadXmlUrl?: string;
    attachedAt: Date | undefined;
    compliances?: Compliances;
  }[];
};

type DocumentaryEvidenceCannotBeProvided = {
  reportMethod: 'manual' | 'automatic';
  reason: string;
};

export type AnalyticalFieldAssociation = {
  fieldKind: 'costCenter' | 'customField';
  fieldEntityId: string;
  fieldEntityValueId: string;
};

export type ItemLine = {
  grossAmount: MonetaryValue;
  netAmount: MonetaryValue;
  taxAmount: MonetaryValue;
  taxAccount:
    | {
        id: string;
        name: string;
        isArchived: boolean;
        rate?: number;
      }
    | undefined;
  taxAccountId?: string | null;
  expenseAccount:
    | {
        id: string;
        name: string;
        isArchived: boolean;
      }
    | undefined;
  expenseAccountId?: string | null;
  analyticalFieldAssociations: AnalyticalFieldAssociation[];
};

export type CustomFieldList = {
  type: 'list';
  id: string;
  name: string;
  values: { id: string; name: string }[];
};

type CustomFieldBoolean = {
  type: 'boolean';
  id: string;
  name: string;
  value: boolean;
};

export type CustomField = CustomFieldList | CustomFieldBoolean;

export type Allocation = {
  entityType: 'settlement' | 'expense' | 'reversal';
  amount: MonetaryValue;
  settlement?: {
    kind: 'payment' | 'refund' | 'reimbursement';
    transaction: {
      medium: 'card' | 'wiretransfer';
      description: string;
      clearingDate: Date;
    };
  };
  reversal?: {
    id: string;
    documentaryEvidence?: {
      creditNoteNumber: string;
    };
  };
  expense?: {
    id: string;
    documentaryEvidence?: {
      invoiceNumber?: string;
    };
  };
};

export type MileageDetails = {
  scheme: {
    type: 'french' | 'german' | 'uk' | 'custom';
    rate: string | null;
  };
  journey: [number, number][];
  departureAddress: string;
  arrivalAddress: string;
  locationsAddresses: string[];
  travelDate: Date;
  distance: string;
  distanceFromMap: string;
  isManualInput: boolean;
  passengers: number;
  vehicle: {
    type: 'car' | 'motorcycle' | 'bike';
    taxHorsepower: number;
    totalDistanceOverYear: string;
    electric: boolean;
    ownership: 'company' | 'personal';
    fuelType: UkVehicleFuelType;
    engineSize: UkVehicleEngineSize;
  };
};

export type PerDiem = {
  duration: number;
};

type CreditNoteDetails = {
  creditNoteNumber: string;
  referenceInvoiceRequestNumber: string | undefined;
  referenceInvoiceRequestId: string | undefined;
  validationAction: CreditNoteValidationAction | undefined;
  state: CreditNote['state'];
};

export type SpendingAllocationPeriod = {
  period: {
    from: Date;
    to: Date;
  };
  date: Date | undefined;
};

export type Payable = {
  id: string;
  version: number;
  kind: 'expense' | 'reversal';
  type: PayableType;
  subtype: PayableSubType | undefined;
  state: PayableState;
  description: string;
  grossAmount: MonetaryValue;
  functionalAmount: MonetaryValue;
  functionalExchangeRate: { amount: number; precision: number };
  firstExportedAt: Date | null;
  counterparty: Member | (Supplier & { type: 'supplier' }) | undefined;
  member: Member | undefined;
  team: Team | undefined;
  supplier: Supplier | undefined;
  costCenter: CostCenter | undefined;
  accountPayable: AccountPayable | undefined;
  documentaryEvidence: DocumentaryEvidence | undefined;
  documentaryEvidenceCannotBeProvided:
    | DocumentaryEvidenceCannotBeProvided
    | undefined;
  itemLines: ItemLine[];
  customFields: CustomField[];
  allocations: Allocation[];
  creationDate: Date;
  mileageDetails: MileageDetails | undefined;
  perDiem: PerDiem | undefined;
  creditNoteDetails: CreditNoteDetails | undefined;
  spendingAllocationPeriod: SpendingAllocationPeriod | undefined;
  accountingDate: Date | undefined;
  amortisation?: PayableAmortisation;
  automation?: PayableAutomation;
  expenseBeneficiaries?: ExpenseBeneficiary[];
};

export const isReversalAllocation = (
  allocation: Allocation,
): allocation is WithRequired<Allocation, 'reversal'> =>
  !isNil(allocation.reversal);
